import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { noop } from 'lodash';
import Icon from 'react-fontawesome';
import styled, { css } from 'styled-components';

import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useIsMobile from 'hooks/useIsMobile';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

import CircularProgress from './CircularProgress';

export enum DataTableTheme {
  DEFAULT = 'default',
  OPEN = 'open',
}
interface TableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
}

type DataTableProps<T> = TableProps<T> & {
  defaultSortBy?: SortingState;
  desktopOnlyColumns?: string[];
  columnVisibility?: Record<string, boolean>;
  hasMore?: boolean;
  loading?: boolean;
  drillable?: boolean;
  hasFooter?: boolean;
  theme?: DataTableTheme;
  maxHeight?: string;
  freezeFirstColumn?: boolean;
  setHoveredRowId?: (hoveredRowId: string) => void;
  onRowClick?: (row: Row<T>) => void;
  onBodyEndReached?: () => void;
  rowStyle?: (row: Row<T>) => React.CSSProperties | undefined;
  // Hierarchical rows: when provided, react-table treats returned children as nested sub-rows that
  // sort within their parent group and render in flattened expanded order.
  getSubRows?: (row: T) => T[] | undefined;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
};

export default function DataTable<T>(props: DataTableProps<T>) {
  const { customRef, isIntersecting } = useIntersectionObserver();
  const {
    defaultSortBy = [],
    desktopOnlyColumns = [],
    columnVisibility = {},
    hasMore = false,
    loading,
    drillable = false,
    setHoveredRowId = noop,
    onRowClick,
    onBodyEndReached,
    rowStyle,
    hasFooter = true,
    theme = DataTableTheme.DEFAULT,
    maxHeight,
    freezeFirstColumn = false,
    getSubRows,
    expanded,
    onExpandedChange,
    ...reactTableProps
  } = props;

  const [sorting, setSorting] = useState<SortingState>(defaultSortBy);

  const isMobile = useIsMobile();

  const table = useReactTable({
    ...reactTableProps,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    ...(getSubRows ? { getSubRows, getExpandedRowModel: getExpandedRowModel(), onExpandedChange } : {}),
    state: {
      columnVisibility: {
        ...desktopOnlyColumns.reduce((acc, key) => ({ ...acc, [key]: !isMobile }), {}),
        ...columnVisibility,
      },
      sorting,
      ...(expanded !== undefined ? { expanded } : {}),
    },
  });

  useEffect(() => {
    if (isIntersecting && hasMore && !loading) {
      onBodyEndReached?.();
    }
  }, [hasMore, isIntersecting, loading, onBodyEndReached]);

  const handleRowClick = (row: Row<T>) => {
    if (drillable && onRowClick) {
      onRowClick(row);
    }
  };

  const hasTableRows = !!table.getRowModel().rows.length;

  const firstHeader = table.getHeaderGroups()[0]?.headers[0];
  const cornerCellRef = useRef<HTMLTableCellElement | null>(null);
  const [cornerSize, setCornerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!freezeFirstColumn || !cornerCellRef.current) return;
    const el = cornerCellRef.current;
    const update = () => setCornerSize({ width: el.offsetWidth, height: el.offsetHeight });
    update();
    // ResizeObserver isn't available in jsdom (test env), so guard the live-resize tracking.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [freezeFirstColumn, firstHeader?.id]);

  return loading && !hasTableRows ? (
    <StyledCircularProgress />
  ) : (
    <ScrollContainer $theme={theme} $maxHeight={maxHeight}>
      {freezeFirstColumn && firstHeader && cornerSize.height > 0 && (
        <CornerOverlay
          style={{
            width: cornerSize.width,
            height: cornerSize.height,
            marginBottom: -cornerSize.height,
          }}>
          <CornerOverlayInner $isSorted={!!firstHeader.column.getIsSorted()}>
            {flexRender(firstHeader.column.columnDef.header, firstHeader.getContext())}
            {firstHeader.column.getCanSort() && (
              <ArrowUpDownIcon
                direction={firstHeader.column.getIsSorted() || 'none'}
                height={10}
                width={10}
                style={{ marginLeft: 8 }}
              />
            )}
          </CornerOverlayInner>
        </CornerOverlay>
      )}
      <Table $theme={theme} $freezeFirstColumn={freezeFirstColumn}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup, groupIdx) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={theme}>
              {headerGroup.headers.map((header, headerIdx) => (
                <TableHeaderCell
                  key={header.id}
                  ref={groupIdx === 0 && headerIdx === 0 ? cornerCellRef : undefined}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  {...header.column.columnDef.meta}
                  width={header.getSize()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <ArrowUpDownIcon
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>

        <TableBody>
          {!hasTableRows && (
            <TableRow>
              <TableCell colSpan={table.getVisibleFlatColumns().length} style={{ justifyContent: 'center' }}>
                No rows found
              </TableCell>
            </TableRow>
          )}
          {hasTableRows &&
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                drillable={drillable}
                style={rowStyle?.(row)}
                onMouseEnter={() => setHoveredRowId(row.id)}
                onMouseLeave={() => setHoveredRowId('')}
                onClick={() => handleRowClick(row)}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} {...cell.column.columnDef.meta} width={cell.column.getSize()}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {/* Empty row used to trigger when the end is reached */}
          <HiddenTableRow ref={customRef}></HiddenTableRow>
          {hasTableRows && loading && (
            <TableRow>
              <TableCell colSpan={table.getVisibleFlatColumns().length}>
                <StyledCircularProgress />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {hasTableRows && hasFooter && (
          <TableFooter>
            {table.getFooterGroups().map((footerGroup) => (
              <TableHeaderFooterRow key={footerGroup.id} $theme={theme}>
                {footerGroup.headers.map((footer) => (
                  <TableFooterCell key={footer.id} {...footer.column.columnDef.meta} width={footer.column.getSize()}>
                    {flexRender(footer.column.columnDef.footer, footer.getContext())}
                  </TableFooterCell>
                ))}
              </TableHeaderFooterRow>
            ))}
          </TableFooter>
        )}
      </Table>
    </ScrollContainer>
  );
}

const StyledCircularProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.primaryBlue};
  margin: auto;
`;

export const ScrollContainer = styled.div<{ $theme: DataTableTheme; $maxHeight?: string }>`
  overflow: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  container-type: inline-size;
  border-radius: 4px;
  max-height: ${({ $maxHeight }) => $maxHeight || '440px'};
  ${({ $theme }) =>
    $theme === DataTableTheme.DEFAULT &&
    css`
      border: 1px solid ${({ theme }) => theme.colors.black10};
    `}
`;

/* Renders on top of the real corner <td> via negative margin. Lives outside the sticky
   <thead>, so its sticky-top/left aren't trapped by a sticky ancestor and compose
   correctly. The real corner cell still exists and scrolls beneath it. */
const CornerOverlay = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 10;
  flex-shrink: 0;
  align-self: flex-start;
  background-color: ${({ theme }) => theme.colors.white};
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: ${({ theme }) => theme.colors.black03};
    pointer-events: none;
  }
`;

const CornerOverlayInner = styled.div<{ $isSorted: boolean }>`
  position: relative;
  z-index: 1;
  font-size: 12px;
  font-weight: bold;
  color: ${({ $isSorted, theme }) => ($isSorted ? theme.colors.black : theme.colors.black50)};
  padding: 16px 24px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

export const Table = styled.table<{ $theme: DataTableTheme; $freezeFirstColumn?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  position: relative;

  /* Row-level dividers so they span the full row width (min-content) instead of clipping to
     tbody's viewport-width box. */
  thead tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  }
  tfoot tr {
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }

  ${({ $freezeFirstColumn }) =>
    $freezeFirstColumn &&
    css`
      /* Freeze the first cell of each row during horizontal scroll. Solid white background so
         the translucent row tints (header black03, sub-row black05, hover primaryBlue05) don't
         let scrolled cells bleed through. The corner cell is visually covered by the
         CornerOverlay rendered outside this <table>, so no top-sticky is needed here. */
      tr > td:first-child {
        position: sticky;
        left: 0;
        background-color: ${({ theme }) => theme.colors.white};
      }

      @media (max-width: 800px) {
        tr > td:first-child {
          max-width: 50cqw;
        }
      }
    `}
`;

export const TableHeader = styled.thead`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ theme }) => theme.colors.white};
`;

export const TableBody = styled.tbody`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.white};

  > * + * {
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }
`;

export const TableRow = styled.tr<{ drillable?: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  min-width: min-content;
  ${({ drillable = false, theme }) =>
    drillable &&
    `
  cursor: pointer;
  &:hover {
    background: ${theme.colors.primaryBlue05};
  }
    `}
`;

const HiddenTableRow = styled(TableRow)`
  border: none;
  height: 1px; /* Needs height to trigger the intersection */
`;

export const TableHeaderFooterRow = styled(TableRow)<{ $theme: DataTableTheme }>`
  flex-basis: 100%;
  ${({ $theme }) =>
    $theme === DataTableTheme.DEFAULT &&
    css`
      background-color: ${({ theme }) => theme.colors.white};
      background-image: linear-gradient(${({ theme }) => theme.colors.black03}, ${({ theme }) => theme.colors.black03});
    `}
`;

export const TableFooter = styled.tfoot`
  display: flex;
`;

export const TableSortIcon = styled(Icon)`
  padding-left: 8px;
  padding-top: 1px;
`;

export const TableCell = styled.td<{ width?: number; bold?: boolean; padding?: string }>`
  align-items: center;
  display: flex;
  flex: ${({ width }) => (width ? width : 150)} 0 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  padding: ${({ padding }) => (padding ? padding : '16px 24px')};
  text-overflow: ellipsis;
  white-space: initial;
  width: ${({ width }) => (width ? width : 150)}px;
`;

export const TableFooterCell = styled(TableCell)`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.black};
`;

export const TableHeaderCell = styled(TableCell)<{ isSorted: boolean; isSortable: boolean }>`
  align-items: normal;
  color: ${(props) => (props.isSorted ? props.theme.colors.black : props.theme.colors.black50)};
  font-weight: bold;
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'default')};
  background-color: ${({ theme }) => theme.colors.white};
  background-image: linear-gradient(${({ theme }) => theme.colors.black03}, ${({ theme }) => theme.colors.black03});
`;
